<template>
  <asc-page-loading-indicator
    :loading="isDerivedParametersLoading && !apiPageFailure.length"
  >
    <v-container v-if="!isDerivedParametersPageActive" fluid>
      <asc-page-coming-soon label="My Parameters page Coming Soon!" />
    </v-container>
    <v-container v-else fluid class="asc-derived-parameter-details-container">
      <asc-sticky :is-sticky="isSticky">
        <v-row :class="{ isSticky: isSticky }">
          <v-col cols="12" sm="12">
            <asc-page-header>
              <template #pageTitle>
                <asc-page-title
                  :class="{ isSticky: isSticky }"
                  :title="getSurfaceTitle"
                ></asc-page-title>
              </template>
              <template #pageActions>
                <asc-derived-parameters-header-wrapper
                  :is-sticky="isSticky"
                  :save-disabled="saveDisabled"
                  :save-loading="saveLoading"
                  @handleSaveAlert="handleSaveAlert"
                  @handleCancel="handleLaunchCancelDialog"
                ></asc-derived-parameters-header-wrapper>
              </template>
            </asc-page-header>
          </v-col>
        </v-row>
      </asc-sticky>
      <asc-notification
        v-if="showLogicValidationNotification"
        :definition="getLogicValidationNotificationDefinition"
        :on-close="handleLogicValidationNotificationClose"
        class="mb-4"
      ></asc-notification>
      <asc-notification
        v-if="showParamaterNameValidationNotification"
        :definition="getParamaterNameValidationNotificationDefinition"
        :on-close="handleLogicValidationNotificationClose"
        class="mb-4"
      ></asc-notification>
      <asc-container>
        <template slot="subheader">
          <asc-surface-support-text
            :text="getSurfaceSupportText"
          ></asc-surface-support-text>
        </template>
        <asc-derived-parameter-details-template
          :parameter-data="derivedParameter"
          :allowed-characters="getAllowedCharacterExpInputs"
          :expression-options="getExpressionOptions"
          :logic-string-valid="logicStringValid"
          :parameter-name-is-valid="parameterNameIsValid"
          :validate-loading="validateLoading"
          :parameter-list="parameterList"
          :is-full-flight="isFullFlight"
          :airline-name="airlineName"
          :airline-id="airlineId"
          :parameter-name="parameterName"
          :logic-string-text="logicStringText"
          :report-type="reportType"
          :alert-type="alertType"
          :is-single-airline-user="isSingleAirlineUser"
          :pdf-guide="logicBuilderGuide"
          :platform-text="getPlatformText"
          @handleLogicValidate="handleLogicValidate"
          @handleExpressionUpdated="handleExpressionUpdated"
          @handleBlurParameterName="handleBlurParameterName"
          @handleChangeParameterName="handleChangeParameterName"
        />
      </asc-container>
      <asc-confirmation
        v-if="showValidLogicConfirmToast"
        :definition="validLogicConfirmToastDefinition"
        :on-close="handleCloseValidLogicConfirmToast"
      ></asc-confirmation>
      <asc-confirmation
        v-if="showValidParameterNameConfirmToast"
        :definition="validParameterConfirmToastDefinition"
        :on-close="handleCloseValidNameConfirmToast"
      ></asc-confirmation>
      <asc-confirmation
        v-if="showDuplicateConfirmationToast"
        :definition="duplicateConfirmationToastDefinition"
        :on-close="handleCloseDuplicateConfirmationToast"
      ></asc-confirmation>
      <asc-dialog
        :show-dialog="showCancelDialog"
        :text="getCancelDialogTitle"
        :on-primary="handleExitAndSave"
        :primary-available="primaryAvailable"
        :on-secondary="handleExitWithoutSaving"
        :on-close="handleCloseCancelDialog"
        secondary-button-text="Exit Without Saving"
        primary-button-text="Save and Exit"
      >
        {{ getCancelDialogText }}
      </asc-dialog>
    </v-container>
  </asc-page-loading-indicator>
</template>

<script>
import AscPageHeader from '@/components/atoms/AscPageHeader'
import AscPageTitle from '@/components/atoms/AscPageTitle'
import AscContainer from '@/components/atoms/AscContainer'
import AscDialog from '@/components/atoms/AscDialog'
import AscSticky from '@/components/atoms/AscSticky'
import AscNotification from '@/components/molecules/AscNotification'
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import AscPageComingSoon from '@/components/molecules/AscPageComingSoon'
import AscSurfaceSupportText from '@/components/atoms/AscSurfaceSupportText.vue'
import AscDerivedParametersHeaderWrapper from '@/components/organisms/AscDerivedParametersHeaderWrapper'
import AscConfirmation from '@/components/molecules/AscConfirmation'

import { AscDerivedParameterDetailsTemplate } from '@/components/templates'

import AppFeatureFlags from '@/utilities/appFeatureFlags'
import {
  getDerivedParametersExpOptions,
  getAllowedDerivedParameterExpInputs,
} from '@/utilities/customAlerts'

import { mapState, mapGetters, mapActions } from 'vuex'
import {
  LOOKUP_CONSTS as LC,
  USER_CONSTS as UC,
  CUSTOM_ALERT_CONSTS as CAC,
  CUSTOM_ALERTS_DERIVED_PARAMETERS_CONSTS as CA_DPC,
  AIRLINE_MANAGEMENT_CONSTS as AOC,
} from '@/stores/consts'

export default {
  name: 'AscDerivedParameterDetailsContainer',

  components: {
    AscPageLoadingIndicator,
    AscPageHeader,
    AscPageTitle,
    AscConfirmation,
    AscContainer,
    AscSticky,
    AscNotification,
    AscDialog,
    AscPageComingSoon,
    AscDerivedParameterDetailsTemplate,
    AscSurfaceSupportText,
    AscDerivedParametersHeaderWrapper,
  },

  props: {
    airlineId: {
      type: Number,
      default: undefined,
    },
    alertType: {
      type: String,
      required: true,
    },
    reportType: {
      type: String,
      default: '',
    },
    isSingleAirlineUser: {
      type: Boolean,
      default: false,
    },
    masterModelId: {
      type: Number,
      default: undefined,
    },
  },

  data() {
    return {
      isSticky: false,
      isDerivedParametersLoading: false,
      validateLoading: false,
      showValidLogicConfirmToast: false,
      showLogicValidationNotification: false,
      showParamaterNameValidationNotification: false,
      showLogicValidatedToast: false,
      showValidParameterNameConfirmToast: false,
      validLogic: false,
      showCancelDialog: false,
      errorMessage: '',
      saveDisabled: false,
      cancelDisabled: true,
      saveLoading: false,
      derivedParameterExpression: '',
      airlineName: '',
      logicStringText: '',
      parameterName: this.parameterName
        ? this.parameterName
        : 'Full Flight Custom Parameter',
      parameterID: this.parameterID,
      showDuplicateConfirmationToast: false,
      primaryAvailable: true,
      duplicateConfirmationToastDefinition: [
        {
          mode: 'toast',
          type: 'warning',
          text: 'Parameter Duplicated',
          position: 'bottom',
          dismissible: true,
          autoClose: true,
          hasToastAction: false,
          icon: 'mdi-check',
          duration: 6000,
        },
      ],
      dataElementIds: [],
      validLogicConfirmToastDefinition: [
        {
          mode: 'toast',
          type: 'success',
          text: 'Full Flight Custom Parameter validated and added.',
          dismissible: true,
          hasToastAction: false,
          autoClose: true,
          duration: 6000,
        },
      ],
      validParameterConfirmToastDefinition: [
        {
          mode: 'toast',
          type: 'success',
          text: 'Name validated.',
          dismissible: true,
          hasToastAction: false,
          autoClose: true,
          duration: 6000,
        },
      ],
    }
  },

  computed: {
    ...mapGetters(UC.NAMESPACE, ['mappedUserAirlines']),
    ...mapState(CAC.NAMESPACE, ['parameterList']),
    ...mapState(CA_DPC.NAMESPACE, [
      'derivedParameter',
      'logicBuilderGuide',
      'isValidParameterName',
      'updatedParameter',
      'derivedParameter',
    ]),
    ...mapState(AOC.NAMESPACE, ['airline']),
    ...mapGetters(LC.NAMESPACE, ['mappedLuMasterModelsAll']),
    ...mapState({ apiPageFailure: 'apiPageFailure' }),

    getSelectedAirline() {
      const selectedAirlineId = this.airlineId
        ? this.airlineId
        : this.$route.query.airlineId
        ? this.$route.query.airlineId
        : undefined

      if (selectedAirlineId) {
        return this.mappedUserAirlines.filter(
          airline => airline.airlineId === Number(selectedAirlineId),
        )
      }

      return undefined
    },

    isDerivedParametersPageActive() {
      return AppFeatureFlags.getDerivedParameters()
    },

    getSurfaceTitle() {
      return 'SET NEW CUSTOM PARAMETER'
    },

    getExpressionOptions() {
      return getDerivedParametersExpOptions()
    },

    getCancelDialogTitle() {
      return `Save Changes?`
    },

    getAllowedCharacterExpInputs() {
      return getAllowedDerivedParameterExpInputs()
    },
    getCancelDialogText() {
      return `This will return you to the custom parameters page. Would you like to save your changes?`
    },
    isFullFlight() {
      const alertType = this.alertType
        ? this.alertType
        : this.$route.query.alertType
        ? this.$route.query.alertType
        : undefined

      return alertType ? alertType === 'fullFlight' : undefined
    },
    logicStringValid: {
      get() {
        if (this.validLogic != null) {
          return this.validLogic
        } else {
          return false
        }
      },
      set() {
        this.validLogic = this.logicStringValid
      },
    },

    getLogicValidationNotificationDefinition() {
      return [
        {
          text: `Logic string not validated: ${this.errorMessage}`,
          mode: 'alert',
          type: 'error',
          dismissible: true,
          isActive: true,
          color: 'error',
        },
      ]
    },
    getParamaterNameValidationNotificationDefinition() {
      return [
        {
          text: `Parameter Name not validated: ${this.errorMessage}`,
          mode: 'alert',
          type: 'error',
          dismissible: true,
          isActive: true,
          color: 'error',
        },
      ]
    },
    getSurfaceSupportText() {
      return this.alertType.includes('fullFlight')
        ? 'Name your custom parameter and begin writing your parameter logic.'
        : 'TBD'
    },
    getPlatformText() {
      let val = this.mappedLuMasterModelsAll.find(
        f => f.masterModelId == this.$route.query.masterModelId,
      )
      return val && val.text ? val.text : ''
    },
  },
  watch: {
    '$route.params.parameterID': function (id) {
      this.parameterID = id
    },
  },
  async mounted() {
    if (this.getSelectedAirline) {
      await this[CAC.GET_FULLFLIGHT_PARAMETER_LIST]({
        airlineIds: this.getSelectedAirline.map(m => {
          return { airlineId: m.airlineId }
        }),
        platformId: Number(this.$route.query.masterModelId),
      })
    }
    if (!this.$route.params.parameterID) {
      if (this.airlineId == undefined || this.airlineName == '') {
        this.validateLoading = true
        await this[AOC.GET_AIRLINE](this.$route.query.airlineId)
        this.airlineName = this.airline.name
        this.validateLoading = false
      }
    }
  },
  async created() {
    this.isDerivedParametersLoading = true

    if (this.alertType === 'fullFlight') {
      const alertTypeSelection = 'DerivedParameterFullFlight'
      if (this.getSelectedAirline) {
        this.airlineName = this.getSelectedAirline[0].airlineName
        this.getCorrespondingLogicBuilderGuide(alertTypeSelection)

        if (this.parameterList.length === 0) {
          await this[CAC.GET_FULLFLIGHT_PARAMETER_LIST]({
            airlineIds: this.getSelectedAirline.map(m => {
              return { airlineId: m.airlineId }
            }),
            platformId: Number(this.$route.query.masterModelId),
          })
        }
      }

      this.validateLoading = false
    }

    if (this.$route.params.parameterID) {
      await this[CA_DPC.GET_DERIVED_PARAMETER](this.$route.params.parameterID),
        (this.showDuplicateConfirmationToast = true)
      this.parameterName = this.derivedParameter.name + ' copy'
      this.logicStringText = this.derivedParameter.logicString

      this.handleBlurParameterName(this.parameterName)
    }
    this.isDerivedParametersLoading = false

    if (
      this.derivedParameterExpression == null ||
      this.derivedParameterExpression.length == 0
    ) {
      this.saveDisabled = true
    }
    if (this.mappedLuMasterModelsAll.length === 0) {
      await this[LC.GET_MASTER_MODELS]()
    }
  },

  methods: {
    ...mapActions(AOC.NAMESPACE, [AOC.GET_AIRLINE]),
    ...mapActions(CAC.NAMESPACE, [CAC.GET_FULLFLIGHT_PARAMETER_LIST]),
    ...mapActions(CA_DPC.NAMESPACE, [CA_DPC.GET_DERIVED_PARAMETER_VALIDATOR]),
    ...mapActions(CA_DPC.NAMESPACE, [
      CA_DPC.GET_DERIVED_PARAMETER_ALERT_LOGIC_BUILDER_GUIDE,
      CA_DPC.UPDATE_DERIVED_PARAMETER_ALERT_LOGIC_BUILDER_GUIDE,
      CA_DPC.SAVE_CUSTOM_PARAMETER,
      CA_DPC.GET_DERIVED_PARAMETER,
      CA_DPC.UPDATE_DERIVED_PARAMETER,
    ]),
    ...mapActions(LC.NAMESPACE, [LC.GET_MASTER_MODELS]),
    async handleExitAndSave() {
      this.saveLoading = true
      this.saveDisabled = !this.isValidParameterName
      this.primaryAvailable = !this.isValidParameterName
      this.derivedParameterExpression = this.derivedParameterExpression
        ? this.derivedParameterExpression
        : this.logicStringText
      this.handleLogicValidate(this.derivedParameterExpression)

      if (this.logicStringValid) {
        this.handleSaveAlert()
      }
    },
    handleExitWithoutSaving() {
      this.showCancelDialog = false
      this.$router.push('/custom-parameters').catch(err => {
        throw new Error(`Problem navigating: ${err}.`)
      })
    },
    async handleLogicValidate(value) {
      this.validateLoading = true
      this.saveLoading = false
      this.handleExpressionUpdated(value)
      if (!value || value.length === 0) {
        this.logicStringIsValid(false, 'Logic String cannot be empty')
        this.showLogicValidationNotification = true
        this.showValidLogicConfirmToast = false
        this.validateLoading = false
        this.saveDisabled = true
        this.primaryAvailable = false
        return
      }

      if (
        this.getNumberOfParameters(value) >
        CA_DPC.DERIVED_PARAMETER_ALERT_PARAMETER_LIMIT
      ) {
        this.logicStringIsValid(
          false,
          `Limit of ${CA_DPC.DERIVED_PARAMETER_ALERT_PARAMETER_LIMIT} distinct parameters exceeded`,
        )
        this.saveLoading = false
        this.saveDisabled = true
      } else {
        //Our initial UI checks have passed and are valid, now time to hit the validator on the API
        this.logicStringIsValid(true, '')
        this.logicStringValid = true
        this.validLogic = true
        this.saveDisabled = false
      }
    },

    async getCorrespondingLogicBuilderGuide(alertTypeSelection) {
      this[CA_DPC.GET_DERIVED_PARAMETER_ALERT_LOGIC_BUILDER_GUIDE](
        alertTypeSelection,
      )
    },

    logicStringIsValid(valid = true, message = '') {
      this.showLogicValidationNotification = !valid
      this.errorMessage = message
      this.validateLoading = false
    },
    handleChangeParameterName(parameterName) {
      if (this.parameterName === parameterName) this.saveDisabled = false
      else this.saveDisabled = true
    },
    async handleBlurParameterName(value) {
      this.validateLoading = true
      if (value) {
        await this[CA_DPC.GET_DERIVED_PARAMETER_VALIDATOR]({
          airlineId: this.$route.query.airlineId
            ? this.$route.query.airlineId
            : this.derivedParameter.airlineId,
          parameterName: value.trim(),
        })
        if (!this.isValidParameterName) {
          this.parameterNameIsValid(
            false,
            'Parameter name cannot be the same as a current or previously deleted parameter',
          )
          this.showParamaterNameValidationNotification = true
          this.showValidParameterNameConfirmToast = false
          this.validateLoading = false
          this.saveDisabled = true
          this.primaryAvailable = false
        } else {
          this.parameterNameIsValid(true)
          this.validateLoading = false
          this.showParamaterNameValidationNotification = false
          this.showValidParameterNameConfirmToast = true
          this.saveDisabled = false
          this.primaryAvailable = true
          this.parameterName = value.trim()
        }
      }

      if (!value || value.length === 0) {
        this.parameterNameIsValid(false, 'Parameter Name can not be empty')
        this.showParamaterNameValidationNotification = true
        this.showValidParameterNameConfirmToast = false
        this.validateLoading = false
        this.saveDisabled = true
        this.primaryAvailable = false
      }
    },
    parameterNameIsValid(valid = true, message = '') {
      this.showParamaterNameValidationNotification = !valid
      this.showValidParameterNameConfirmToast = valid
      this.errorMessage = message
    },
    getNumberOfParameters(expr) {
      const re = /\[.*?\]/gi
      const count = ((expr || '').match(re) || []).length
      return count
    },
    handleLaunchCancelDialog() {
      this.showCancelDialog = true
    },
    handleCloseCancelDialog() {
      this.showCancelDialog = false
    },
    async handleSaveAlert() {
      this.saveLoading = true
      this.saveDisabled = true
      this.hasSaved = true

      await this.handleLogicValidate(this.derivedParameterExpression)

      if (this.logicStringValid && this.isValidParameterName) {
        this.saveDisabled = true
        await this[CA_DPC.SAVE_CUSTOM_PARAMETER]({
          logicString: this.derivedParameterExpression,
          airlineId: this.$route.query.airlineId
            ? this.$route.query.airlineId
            : this.derivedParameter.airlineId,
          parameterName: this.parameterName,
          alertType: this.alertType,
          dataElementIds: this.dataElementIds,
          isActive: true,
          masterModelId: Number(this.$route.query.masterModelId),
        }).catch(err => {
          throw new Error(`Unable to save custom parameter: ${err}.`)
        })

        if (this.derivedParameter.validLogic && this.isValidParameterName) {
          this.showLogicValidationErrorMessage = false
          this.showValidLogicConfirmToast = true
          setTimeout(
            () =>
              this.$router.push({
                name: 'Custom Parameters',
              }),
            6000,
          )
        } else if (this.derivedParameter.errorMessage) {
          this.logicStringIsValid(false, this.derivedParameter.errorMessage)
          this.showValidLogicConfirmToast = false
          this.showLogicValidationErrorMessage = true
          this.saveDisabled = false
          this.primaryAvailable = true
          this.saveLoading = false
          this.validateLoading = false
        } else if (
          !this.derivedParameterExpression ||
          this.derivedParameterExpression.length === 0
        ) {
          this.logicStringIsValid(false, 'Logic String cannot be empty')
          this.showLogicValidationNotification = true
          this.showValidLogicConfirmToast = false
          this.validateLoading = false
          this.saveDisabled = false
          this.primaryAvailable = true
          this.saveLoading = false
        }
      } else {
        this.saveLoading = false
        this.validateLoading = false
        if (!this.isValidParameterName) {
          this.showParamaterNameValidationNotification = true
          this.showValidParameterNameConfirmToast = false
          this.validateLoading = false
          this.saveDisabled = true
          this.primaryAvailable = false
        }
      }
    },

    handleExpressionUpdated(value) {
      this.derivedParameterExpression = value

      if (
        this.derivedParameterExpression.length > 0 &&
        this.parameterName.length > 0
      ) {
        this.saveDisabled = false
        this.primaryAvailable = true
        this.cancelDisabled = false
        this.dataElementIds = []
      } else {
        this.saveDisabled = true
        this.primaryAvailable = false
        this.cancelDisabled = true
      }

      if (value.includes('[') && value.includes(']')) {
        const re = /\[.*?\]/gi
        let uniqueParms = (value || '').match(re) || []

        if (uniqueParms.length > 0) {
          for (let i = 0; i < uniqueParms.length; i++) {
            let parmString = uniqueParms[i].substring(
              1,
              uniqueParms[i].length - 1,
            )
            let parameter = this.parameterList.find(
              x => x.rawName === parmString,
            )

            if (parameter) {
              this.dataElementIds.push(parameter.dataElementId)
            } else {
              this.saveDisabled = true
              this.logicStringIsValid(
                false,
                'Invalid parameter found, please try again with a valid parameter',
              )
            }
          }
        }
      } else {
        this.saveDisabled = true
        this.primaryAvailable = false
        this.cancelDisabled = true
      }
    },
    handleCloseValidNameConfirmToast() {
      this.showValidParameterNameConfirmToast = false
      this.validateLading = false
    },
    handleCloseDuplicateConfirmationToast() {
      this.showDuplicateConfirmationToast = false
    },
    handleCloseValidLogicConfirmToast() {
      this.showValidLogicConfirmToast = false
      this.validateLoading = false
    },
    handleLogicValidationNotificationClose() {
      this.showLogicValidationNotification = false
      this.validateLoading = false
    },
  },
}
</script>
